import {useState} from 'react';
import {ConstantValue, Question} from '@agenium-ai/agenium-client-sdk/dist/models';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import {makeStyles} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import FormGroup from '@material-ui/core/FormGroup';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Radio from '@material-ui/core/Radio';
import Checkbox from '@material-ui/core/Checkbox';
import {truthy} from '../../../Core/Utils';
import Box from '@material-ui/core/Box';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';
import Snackbar from '@material-ui/core/Snackbar';
import Slide from '@material-ui/core/Slide';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';

export interface MultipleChoiceMultipleInputProps {
    question: Question;
    onChange?: (s: string) => void;
    value: string;
}

interface ExtraConstantValue extends ConstantValue {
    helpText?: string;
    helpTextExpanded?: string;
}

const JOIN_TOKEN = ', ';

const useStyles = makeStyles(() => ({
    title: {
        marginBottom: 40,
        fontFamily: 'LoraRegular',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: '32px',
        lineHeight: '120%',
        color: '#456d69',
        '& p': {
            margin: 0,
            padding: 0,
        },
        '& p + p': {
            fontFamily: 'FredokaSemiBold',
            fontStyle: 'normal',
            fontWeight: 500,
            fontSize: '18px',
            lineHeight: '150%',
            color: '#456d69',
        },
    },
    tooltipFloating: {
        minWidth: '600px',
        background: '#fff',
        boxShadow: '5px 5px 10px rgba(100,100,100,0.5)',
        border: '1px solid #888',
    },
    showAllBtn: {
        display: 'none',
        textTransform: 'capitalize',
        p: 0,
        verticalAlign: 'baseline',
        color: '#949494',
        fontWeight: 600,
        '&.MuiButton-textSizeSmall': {
            fontSize: '14px',
        },
        '@media (max-width: 767px)': {
            display: 'inline',
        },
    },
    showAllBtnLargeScreen: {
        display: 'inline',
        p: 0,
        paddingLeft: 4,
        color: '#949494',
        fontWeight: 600,
        fontSize: '14px',
        '@media (max-width: 767px)': {
            display: 'none',
        },
    },
    displayName: {
        color: '#174943',
        fontSize: '26px',
        fontFamily: 'LoraRegular',
        lineHeight: '28.6px',
        paddingBottom: '10px',
    },
    helpTextExpanded: {
        color: '#000',
        fontSize: '16px',
        fontFamily: 'Fredoka',
        lineHeight: '25.6px',
        fontWeight: 300,
        p: '0 10px 10px 10px',
    },
    snackBar: {
        width: '100%',
        left: '0 !important',
        bottom: '0 !important',
        right: '0 !important',
        height: '100%',
        background: 'rgba(0,0,0,0.5)',
        flexDirection: 'column-reverse',
        '& .MuiPaper-root': {
            border: 'none',
            borderRadius: '16px 16px 0 0',
            background: '#fff',
            color: '#000',
        },
        transform: 'none !important',
        '& .MuiAlert-action': {
            marginRight: '10px',
        },
    },
    checked: {
        width: 24,
        height: 24,
        borderRadius: 4,
        border: '1.5px solid #174943',
        backgroundColor: '#5AE288',
        boxSizing: 'border-box',
        padding: 2,
    },
    checkbox: {
        width: 24,
        height: 24,
        borderRadius: 4,
        border: '1.5px solid #174943',
        boxSizing: 'border-box',
        padding: 2,
    },
    checkboxRoot: {
        position: 'relative',
        paddingRight: 24,
        '&::after': {
            content: '""',
            position: 'absolute',
            width: 16,
            height: 16,
            borderRadius: 2,
            backgroundColor: 'transparent',
        },
        '&.MuiIconButton-root:hover': {
            backgroundColor: 'transparent',
        },
    },
    label: {
        color: '#000',
        fontSize: 18,
        fontFamily: 'FredokaLight',
        lineHeight: '120%',
        fontWeight: 300,
    },
    checkboxContainer: {
        '&:hover $checkboxRoot::after': {
            backgroundColor: '#D9D9D9',
        },
        '&:hover .Mui-checked$checkboxRoot::after': {
            backgroundColor: 'transparent',
        },
    },
}));

const MultipleChoiceMultipleInput = (props: MultipleChoiceMultipleInputProps) => {
    const classes = useStyles();
    const [snackBarShow, setSnackShow] = useState<{id?: string; title?: string; content?: string}>();
    const {question, value, onChange} = props;
    const splitValue = value && typeof value === 'string' ? value.split(JOIN_TOKEN) : [];

    function handleChange(id: string, isChecked: boolean) {
        let newValue: string[];
        if (id === 'None of the above' || id === 'Never') {
            newValue = [id];
            const valueString = newValue.filter(truthy).join(JOIN_TOKEN);
            onChange?.(valueString);
        } else {
            if (isChecked) newValue = [...splitValue.filter((v) => v !== 'None of the above' && v !== 'Never'), id];
            else newValue = splitValue.filter((v) => v !== id);
            const valueString = newValue.filter(truthy).join(JOIN_TOKEN);
            onChange?.(valueString);
        }
    }

    function expandHelpTextForId(id: string) {
        const q = question?.allowedValues.find((q) => q.id === id) as ExtraConstantValue;
        setSnackShow({id, title: q?.displayName ?? '', content: q?.helpTextExpanded ?? ''});
    }

    return (
        <>
            <Typography component="h1" variant="h4" classes={{root: classes.title}}>
                {parse(DOMPurify.sanitize(question.questionText, {USE_PROFILES: {html: true}}))}
            </Typography>
            <FormGroup>
                {(question?.allowedValues as ExtraConstantValue[])?.map(
                    ({id, displayName, helpText, helpTextExpanded}) =>
                        id === 'None of the above' || id === 'Never' ? (
                            <RadioGroup key={`radio-${id}`}>
                                <FormControlLabel
                                    key={`radioid-${id}`}
                                    checked={splitValue?.includes(id)}
                                    classes={{label: classes.label}}
                                    control={
                                        <Radio
                                            disableRipple
                                            classes={{root: classes.checkboxRoot}}
                                            checkedIcon={
                                                <img src="/img/checked.svg" alt="Checked" className={classes.checked} />
                                            }
                                            icon={<Box className={classes.checkbox} />}
                                            onChange={(e) => handleChange(id, e.target.checked)}
                                        />
                                    }
                                    label={displayName}
                                />
                            </RadioGroup>
                        ) : (
                            <FormControlLabel
                                key={`checkboxid-${id}`}
                                control={
                                    <Checkbox
                                        disableRipple
                                        color="primary"
                                        classes={{root: classes.checkboxRoot}}
                                        name={question.key}
                                        checked={splitValue?.includes(id)}
                                        onChange={(e) => handleChange(id, e.target.checked)}
                                        checkedIcon={
                                            <img src="/img/checked.svg" alt="Checked" className={classes.checked} />
                                        }
                                        icon={<Box className={classes.checkbox} />}
                                    />
                                }
                                classes={{root: classes.checkboxContainer, label: classes.label}}
                                label={
                                    helpTextExpanded ? (
                                        <Box style={{paddingTop: '10px', paddingBottom: '10px'}}>
                                            <Box>{displayName}</Box>
                                            <Box
                                                style={{
                                                    color: '#949494',
                                                    fontSize: '14px',
                                                    fontWeight: 300,
                                                }}
                                            >
                                                {helpText}
                                                {`, `}
                                                <Button
                                                    variant="text"
                                                    size="small"
                                                    classes={{root: classes.showAllBtn}}
                                                    onClick={() => expandHelpTextForId(id)}
                                                >
                                                    Show All
                                                </Button>
                                                <Tooltip
                                                    disableFocusListener
                                                    classes={{tooltip: classes.tooltipFloating}}
                                                    title={
                                                        <>
                                                            <Typography classes={{root: classes.displayName}}>
                                                                {displayName}
                                                            </Typography>
                                                            <Typography classes={{root: classes.helpTextExpanded}}>
                                                                {helpTextExpanded}
                                                            </Typography>
                                                        </>
                                                    }
                                                    placement="top"
                                                >
                                                    <Typography
                                                        component="span"
                                                        classes={{root: classes.showAllBtnLargeScreen}}
                                                    >
                                                        Show All
                                                    </Typography>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    ) : (
                                        displayName
                                    )
                                }
                            />
                        ),
                )}
                <Snackbar
                    open={!!snackBarShow?.id}
                    key={Slide.name}
                    TransitionComponent={Slide}
                    classes={{root: classes.snackBar}}
                >
                    <Alert
                        icon={<></>}
                        onClose={() => setSnackShow({})}
                        severity="success"
                        style={{
                            width: '100%',
                            border: '1px solid #888',
                            overflowY: 'auto',
                            position: 'fixed',
                            bottom: 0,
                            padding: 0,
                        }}
                    >
                        <AlertTitle
                            style={{
                                color: '#174943',
                                fontSize: '26px',
                                padding: '10px 10px 0 10px',
                                fontFamily: 'LoraRegular',
                                lineHeight: '28.6px',
                            }}
                        >
                            {snackBarShow?.title}
                        </AlertTitle>
                        <Typography
                            style={{
                                color: '#000',
                                padding: '10px',
                                fontSize: '16px',
                                fontFamily: 'FredokaRegular',
                                lineHeight: '25.6px',
                                fontWeight: 300,
                            }}
                        >
                            {snackBarShow?.content}
                        </Typography>
                    </Alert>
                </Snackbar>
            </FormGroup>
        </>
    );
};

export default MultipleChoiceMultipleInput;
